import { PowerSyncDatabase } from "@powersync/web";
import { AppSchema } from "./schema";
import { RailsConnector } from "./connector";
import { seedDemo, DEMO_SEED_VERSION } from "./demo";

export type DataMode = "live" | "demo";

// Two physically separate local databases. The demo file is seeded locally and
// is NEVER connected to a sync service, so fixture rows can never upload.
function open(file: string) {
  return new PowerSyncDatabase({
    schema: AppSchema,
    database: { dbFilename: file },
  });
}

let current: { mode: DataMode; db: PowerSyncDatabase } | null = null;
let pending: { key: string; p: Promise<PowerSyncDatabase> } | null = null;

// Single-flight: React StrictMode (and fast re-renders) may ask twice.
export function openDatabase(mode: DataMode, userId?: string): Promise<PowerSyncDatabase> {
  const key = `${mode}:${userId ?? ""}`;
  if (pending?.key === key) return pending.p;
  const p = doOpen(mode, userId).finally(() => { if (pending?.p === p) pending = null; });
  pending = { key, p };
  return p;
}

async function doOpen(mode: DataMode, userId?: string): Promise<PowerSyncDatabase> {
  if (current?.mode === mode) return current.db;
  if (current) await current.db.close();
  if (mode === "demo") {
    const db = open(`propelmapper-demo-${DEMO_SEED_VERSION}.sqlite`); // bump the version to reseed
    await db.init();
    await seedDemo(db);
    current = { mode, db };
    return db;
  }
  // One file per user so a shared computer never shows another user's data.
  const db = open(`propelmapper-${userId ?? "anon"}.sqlite`);
  await db.init();
  await db.connect(new RailsConnector());
  current = { mode, db };
  return db;
}

export async function closeDatabase(clear = false) {
  if (!current) return;
  const { db, mode } = current;
  current = null;
  if (mode === "live") await db.disconnectAndClear?.().catch(() => {});
  if (clear) await db.close();
}
